import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  ACTIVE_LOCATION,
  ACTIVE_USER,
  CHEVRON_DOWN,
  CLOSE,
  DOC,
  INACTIVE_LOCATION,
  INACTIVE_USER,
  INFO,
} from "../../../assets";
import clsx from "clsx";
import ButtonV2 from "../buttonV2";
import useDropdown from "../../../utils-func/functions/usedropdown";
import { useQuery } from "react-query";
import {
  getLocalGovernment,
  getStates,
  initiateSearch,
  updateApplicationPayment,
} from "../../../Services/services";
import { useInterswitchPay } from "../../../hooks/useInterswitchPay";
import { toast } from "react-hot-toast";
import { SearchTypes } from "../../../types";
import { Tooltip } from "@mui/material";
import { propertyNames } from "../caseformModal";
import PaymentGatewayModal from "../paymentGatewayModal";

const steps = [
  {
    label: "Property Details",
    step: 1,
    icon: DOC,
  },
  {
    label: "Location",
    step: 2,
    icon: ACTIVE_LOCATION,
    inactive: INACTIVE_LOCATION,
  },
  {
    label: "User",
    step: 3,
    icon: ACTIVE_USER,
    inactive: INACTIVE_USER,
  },
];
function PropertyModal({ open, close }: { open: boolean; close: () => void }) {
  const [activeStep, setActiveStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const { pay: openInterswitchModal } = useInterswitchPay();
  const { dropdownRef, dropdowns, setDropdowns, closeDropDown } = useDropdown();
  const [fields, setFields] = useState({
    title: "",
    title_number: "",
    owner_name: "",
    survey_plan_number: "",
    street: "",
    city: "",

    plot_number: "",
  });
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);
  const [paymentId, setPaymentId] = useState(null);
  const [selectedState, setSelectedState] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [selectedLga, setSelectedLga] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [selectedProperty, setSelectedProperty] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const { data: localGovernments } = useQuery(["lga", currentPage], () =>
    getLocalGovernment(currentPage),
  );
  const { data: states } = useQuery("states", getStates);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFields((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleInitiateSearch = async () => {
    if (
      (!selectedProperty?.name && !fields.title) ||
      (selectedProperty?.name === "Others" && !fields.title) ||
      !selectedLga?.id ||
      !states?.[0]?.id
    ) {
      toast.error("Please fill all required fields");
      return;
    }
    setLoading(true);
    const payload: SearchTypes = {
      title: selectedProperty?.name! || fields.title,
      title_number: fields.title_number,
      owner_name: fields.owner_name,
      street: fields.street,
      city: fields.city,
      plot_number: fields.plot_number,
      survey_plan_number: fields.survey_plan_number,
      lga_id: selectedLga?.id,
      state_id: states[0]?.id,
      // payment_method: gateway.provider,
      // callback_url: `${window.location.origin}/individual/search-result`,
    };
    try {
      const response = await initiateSearch(payload);
      if (response) {
        // toast.success(response?.message);
        setPaymentId(response?.data?.payment_id);
        setIsPaymentModalOpen(true);
        // window.location.href = response?.data?.url;
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPaymentMethod = async (id: number | string) => {
    if (!id) {
      toast.error("payment id is required");
      return;
    }

    setIsSubmittingPayment(true);
    try {
      const response = await updateApplicationPayment(paymentId!, {
        callback_url: `${window.location.origin}/individual/search-result`,
        payment_gateway_id: id.toString(),
      });

      const url: string = response?.data?.url;
      if (!url) return;

      setIsPaymentModalOpen(false);

      if (url.includes("interswitchng.com")) {
        // Interswitch — use inline modal (no redirect)
        openInterswitchModal({
          checkoutUrl: url,
          amountInNaira: response.data.amount,
          onSuccess: async (txnRef: string) => {
            try {
              window.location.href = `${window.location.origin}/individual/search-result?reference=${encodeURIComponent(txnRef)}`;
            } catch (e) {
              // verification handled server-side; proceed to result page
            }
          },
          onError: () => {
            toast.error("Payment was not completed. Please try again.");
          },
        });
      } else {
        // Paystack / Moniepoint / others — use normal redirect
        window.location.href = url;
      }
    } catch (error) {
      toast.error("Failed to update payment method. Please try again.");
    } finally {
      setIsSubmittingPayment(false);
    }
  };

  return (
    <>
      <div
        className={`w-full h-full fixed ${
          open ? "block" : "hidden"
        } top-0 left-0 bg-black flex items-center justify-center bg-opacity-50 z-[999]`}
      >
        {open && (
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 50, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="w-[600px] bg-white rounded-xl shadow-xl right-6 z-50"
          >
            <div className="w-full flex items-center justify-between bg-lightGold p-5">
              <h2 className="text-[16px] font-semibold text-[#2A2E34]">
                Property Search
              </h2>
              <button
                onClick={close}
                className="w-[30px] h-[30px] rounded-full -mt-2 flex items-center justify-center hover:bg-gray-200"
              >
                <img src={CLOSE} className="w-[20px] h-[20px]" alt="" />
              </button>
            </div>
            <div className="w-full flex items-center justify-between  px-5 py-4">
              {steps.map((item) => (
                <div
                  className="flex items-center gap-x-1 cursor-pointer"
                  onClick={() => setActiveStep(item.step)}
                >
                  <img
                    src={
                      activeStep > item.step || activeStep === item.step
                        ? item.icon
                        : item.inactive
                    }
                    className="w-[20px] h-[20px]"
                    alt=""
                  />
                  <span
                    className={clsx(
                      "text-sm font-medium",
                      activeStep > item.step || activeStep === item.step
                        ? "text-globalGold"
                        : "text-primary-50",
                    )}
                  >
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
            <div className="w-full bg-lightGold rounded h-[2.5px] overflow-hidden mb-6">
              <div
                className="h-full bg-globalGold transition-all duration-700 ease-in-out"
                style={{
                  width: `${(activeStep / 3) * 100}%`,
                }}
              />
            </div>

            {activeStep === 1 && (
              <div className="flex flex-col w-full gap-y-6 items-start px-6">
                <div className="w-full flex items-center gap-x-2">
                  <div className="w-full flex flex-col gap-y-2 relative">
                    <span className="text-sm font-medium text-[#4F5762]">
                      Type of Property Title{" "}
                      <span className="text-red-500">*</span>
                    </span>
                    {selectedProperty?.name === "Others" ? (
                      <input
                        type="text"
                        name="title"
                        autoFocus
                        value={fields.title}
                        onChange={handleInputChange}
                        placeholder="Enter Property Title"
                        className="w-full bg-transparent outline-none p-3 rounded-xl border border-borderColor shadow-box"
                      />
                    ) : (
                      <>
                        <div
                          onClick={() =>
                            setDropdowns({ ...dropdowns, property: true })
                          }
                          className="flex items-center justify-between  gap-x-3 p-3 cursor-pointer rounded-xl border border-borderColor shadow-box"
                        >
                          <input
                            type="text"
                            placeholder="Select property title"
                            value={selectedProperty?.name}
                            className="w-full bg-transparent outline-none placeholder:font-normal placeholder:text-sm placeholder:text-[#808080]"
                          />

                          <img
                            src={CHEVRON_DOWN}
                            className="w-[20px] h-[20px]"
                            alt=""
                          />
                        </div>
                        {propertyNames.length > 0 && dropdowns.property && (
                          <div
                            ref={dropdownRef.property}
                            className="w-full bg-white absolute z-10 shadow-xl rounded-md mt-1 flex flex-col max-h-60 overflow-y-auto animate-dropdown"
                          >
                            {propertyNames?.map((item) => (
                              <span
                                key={item.id}
                                onClick={() => {
                                  setSelectedProperty(item);
                                  closeDropDown("property");
                                }}
                                className="px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 cursor-pointer transition-colors"
                              >
                                {item.name}
                              </span>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                    {/* <span className="text-red-500 text-xs">
                                        {errors?.title && erros?.title[0]}
                                      </span> */}
                  </div>
                  <div className="w-full flex flex-col gap-y-2">
                    <span className="text-sm font-medium text-[#4F5762]">
                      Registered Title Number
                    </span>
                    <input
                      type="text"
                      name="title_number"
                      value={fields.title_number}
                      onChange={handleInputChange}
                      placeholder="Enter registered title number"
                      className="w-full bg-transparent outline-none p-3 rounded-xl border border-borderColor shadow-box"
                    />
                  </div>
                </div>
                <div className="w-full flex flex-col gap-y-2">
                  <span className="text-sm font-medium text-[#4F5762]">
                    Survey Plan Number
                  </span>
                  <input
                    type="text"
                    name="survey_plan_number"
                    value={fields.survey_plan_number}
                    onChange={handleInputChange}
                    placeholder="Enter survey plan number"
                    className="w-full bg-transparent outline-none p-3 rounded-xl border border-borderColor shadow-box"
                  />
                </div>
              </div>
            )}
            {activeStep === 2 && (
              <div className="flex flex-col w-full gap-y-6 items-start px-6">
                <div className="w-full flex items-center gap-x-2">
                  <div className="w-full flex flex-col gap-y-2">
                    <span className="text-sm font-medium text-[#4F5762]">
                      Plot Number
                    </span>
                    <input
                      type="text"
                      name="plot_number"
                      value={fields.plot_number}
                      onChange={handleInputChange}
                      placeholder="Enter plot number"
                      className="w-full bg-transparent outline-none p-3 rounded-xl border border-borderColor shadow-box"
                    />
                  </div>
                  <div className="w-full flex flex-col gap-y-2">
                    <span className="text-sm font-medium text-[#4F5762]">
                      Street Name
                    </span>
                    <input
                      type="text"
                      name="street"
                      value={fields.street}
                      onChange={handleInputChange}
                      placeholder="Enter street name"
                      className="w-full bg-transparent outline-none p-3 rounded-xl border border-borderColor shadow-box"
                    />
                  </div>
                </div>
                <div className="w-full flex items-center gap-x-2">
                  <div className="w-full flex flex-col gap-y-2">
                    <span className="text-sm font-medium text-[#4F5762]">
                      City/Town
                    </span>
                    <input
                      type="text"
                      name="city"
                      value={fields.city}
                      onChange={handleInputChange}
                      placeholder="Enter city/town"
                      className="w-full bg-transparent outline-none p-3 rounded-xl border border-borderColor shadow-box"
                    />
                  </div>
                  <div className="w-full flex flex-col gap-y-2 relative">
                    <span className="text-sm font-medium text-[#4F5762]">
                      State
                      <span className="text-red-500">*</span>
                    </span>
                    <div
                      onClick={() =>
                        setDropdowns({ ...dropdowns, state: true })
                      }
                      className="flex items-center justify-between  gap-x-3 p-3 cursor-pointer rounded-xl border border-borderColor shadow-box"
                    >
                      <input
                        type="text"
                        placeholder="Enter state"
                        value={selectedState?.name}
                        className="w-full bg-transparent outline-none "
                      />

                      <img
                        src={CHEVRON_DOWN}
                        className="w-[20px] h-[20px]"
                        alt=""
                      />
                    </div>
                    {states?.length > 0 && dropdowns.state && (
                      <div
                        ref={dropdownRef.state}
                        className="w-full bg-white absolute z-10 shadow-xl rounded-md mt-1 flex flex-col max-h-60 overflow-y-auto animate-dropdown"
                      >
                        {states?.map((item: { id: number; name: string }) => (
                          <span
                            key={item.id}
                            onClick={() => {
                              setSelectedState(item);
                              closeDropDown("state");
                            }}
                            className="px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 cursor-pointer transition-colors"
                          >
                            {item.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="w-full flex flex-col gap-y-2 relative">
                  <span className="text-sm font-medium text-[#4F5762]">
                    Local Government Area (LGA)
                    <span className="text-red-500">*</span>
                  </span>
                  <div
                    onClick={() => setDropdowns({ ...dropdowns, lga: true })}
                    className="flex items-center justify-between gap-x-3 p-3 cursor-pointer rounded-xl border border-borderColor shadow-box"
                  >
                    <input
                      type="text"
                      value={selectedLga?.name}
                      placeholder="Enter local govt area (LGA)"
                      className="w-full bg-transparent outline-none "
                    />
                    {/* <span className="text-sm font-medium text-customDark">
                      {selectedLga?.name || "Enter local govt area (LGA)"}
                    </span> */}
                    <img
                      src={CHEVRON_DOWN}
                      className="w-[20px] h-[20px]"
                      alt=""
                    />
                  </div>
                  {localGovernments?.data?.length > 0 && dropdowns.lga && (
                    <div
                      ref={dropdownRef.lga}
                      className="w-full bg-white absolute z-10 bottom-10 shadow-xl rounded-md mt-1 flex flex-col max-h-60 overflow-y-auto animate-dropdown"
                    >
                      {localGovernments?.data?.map(
                        (item: {
                          id: number;
                          name: string;
                          state_id: number;
                          created_at: string;
                          updated_at: string;
                        }) => (
                          <span
                            key={item.id}
                            onClick={() => {
                              setSelectedLga(item);
                              closeDropDown("lga");
                            }}
                            className="px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 cursor-pointer transition-colors"
                          >
                            {item.name}
                          </span>
                        ),
                      )}
                      <div className="flex items-center gap-x-2 justify-center">
                        <Tooltip title="Previous">
                          <button
                            onClick={() =>
                              setCurrentPage(
                                localGovernments?.meta?.current_page - 1,
                              )
                            }
                            disabled={
                              localGovernments?.meta?.current_page === 1
                            }
                            className={`w-[30px] h-[30px] rotate-180 hover:bg-gray-100 rounded-full flex items-center justify-center ${
                              localGovernments?.meta?.current_page === 1 &&
                              "cursor-not-allowed"
                            }`}
                          >
                            <img src={CHEVRON_DOWN} alt="" />
                          </button>
                        </Tooltip>
                        <Tooltip title="Next">
                          <button
                            onClick={() =>
                              setCurrentPage(
                                localGovernments?.meta?.current_page + 1,
                              )
                            }
                            disabled={
                              localGovernments?.meta?.current_page ===
                              localGovernments?.meta?.last_page
                            }
                            className={`w-[30px] h-[30px] hover:bg-gray-100 rounded-full flex items-center justify-center ${
                              localGovernments?.meta?.current_page ===
                                localGovernments?.meta?.last_page &&
                              "cursor-not-allowed"
                            }`}
                          >
                            <img src={CHEVRON_DOWN} alt="" />
                          </button>
                        </Tooltip>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {activeStep === 3 && (
              <div className="flex w-full flex-col gap-y-4 px-6">
                <div className="w-full flex flex-col gap-y-2">
                  <span className="text-sm font-medium text-[#4F5762]">
                    Parties to the suit
                  </span>
                  <div className="flex items-center gap-x-3 p-3 rounded-xl border border-borderColor shadow-box">
                    <input
                      type="text"
                      name="owner_name"
                      placeholder="Parties to the suit"
                      value={fields.owner_name}
                      onChange={handleInputChange}
                      className="w-full bg-transparent outline-none "
                    />
                  </div>
                </div>
                <div className="w-full bg-lightGold flex items-center gap-x-2 p-6 rounded-xl">
                  <img src={INFO} className="w-[20px] h-[20px]" alt="" />
                  <span className="text-sm font-normal text-globalGold">
                    providing the parties to the suit can help improve search
                    accuracy
                  </span>
                </div>
              </div>
            )}

            <div className="flex items-end justify-end gap-x-3 p-8">
              {activeStep > 1 && (
                <ButtonV2
                  title={"Back"}
                  btnStyle={
                    "w-fit h-fit px-7 py-2 rounded-xl border border-borderColor"
                  }
                  textStyle={"text-sm font-medium text-customDark"}
                  handleClick={() => setActiveStep((prev) => prev - 1)}
                />
              )}
              <ButtonV2
                title={activeStep === 3 ? "Search Property" : "Next"}
                btnStyle={"w-fit h-fit  px-7 py-2 rounded-xl bg-globalGold"}
                textStyle={"text-sm font-medium text-white "}
                loading={loading}
                loaderColor="#ffffff"
                handleClick={() => {
                  if (activeStep === 3) {
                    handleInitiateSearch();
                  } else {
                    setActiveStep((prev) => prev + 1);
                  }
                }}
              />
            </div>
          </motion.div>
        )}
        <PaymentGatewayModal
          isOpen={isPaymentModalOpen}
          onClose={() => setIsPaymentModalOpen(false)}
          onSelect={handleSelectPaymentMethod}
          isSubmitting={isSubmittingPayment}
        />
      </div>
    </>
  );
}

export default PropertyModal;
